/* eslint-disable no-undef */
import axios from "axios";
import {
  loginDataSuccess,
  loginUserLoginSuccess,
} from "../actions/userActions";
import storeConfig from "../store";
import { resolveTenant,isCustomDomain  } from "./customDomains";

const DEV_HOSTS = process.env.REACT_APP_DEV_HOSTS?.split(",") || [];

const getSubdomain = () => {
  // Get the full hostname
  const hostname = globalThis.location.hostname;

  if (DEV_HOSTS.includes(hostname)) {
    return "dev-upgraded";
  }
  // Client-owned masked domains (e.g. mentoring.sid.org.sg) map to a tenant
  // explicitly — subdomain parsing below would yield a wrong value for them.
  const customTenant = resolveTenant(hostname);
  if (customTenant) {
    return customTenant;
  }

  // Split the hostname into parts
  const parts = hostname.split(".");

  // Check if there's a subdomain
  if (parts.length > 2) {
    // Return the subdomain
    return parts.slice(0, -2).join("."); // Join parts back to a string
  } else {
    alert("Please check the domain and try again");
    globalThis.location.href = "https://www.vlookup.ai";
  }
};

const xSimVAlue = getSubdomain();
// Masked domains call the API same-origin (their vhost serves /api on the same
// box), keeping the client domain in the address bar and avoiding CORS.
let apiURL = isCustomDomain(globalThis.location.hostname)
  ? process.env.REACT_APP_API_BASE_URL.replace(
      "{{subdomain}}.vlookup.ai",
      globalThis.location.hostname,
    )
  : process.env.REACT_APP_API_BASE_URL.replace("{{subdomain}}", xSimVAlue);

const agent = axios.create({
  baseURL: apiURL,
  headers: {
    "Content-Type": "application/json",
    "X-Env": xSimVAlue,
    Accept: "application/json",
  },
});

function decodeCookieString(cookieString) {
  if (!cookieString) return {};

  return cookieString.split("; ").reduce((acc, cookie) => {
    if (!cookie) return acc;

    const parts = cookie.split("=");
    const [key, ...value] = parts;

    if (!key) return acc;

    acc[key] = decodeURIComponent(value.join("="));
    return acc;
  }, {});
}

function parseCookieJson(str) {
  if (!str || str === "undefined") return null;

  try {
    // Remove leading j:
    const cleaned = str.replace(/^j:/, "");
    return JSON.parse(cleaned);
  } catch (error) {
    console.error("Invalid JSON in cookie:", str, error);
    return null;
  }
}
// Turn the cookie value into a clean true / false / null.
// The value can arrive as true, 1 or "1" (eligible) — or false, 0 or "0" (not eligible).
// Anything else means "unknown" (null) — the role popup treats unknown as Mentee.
function normalizeEligibility(value) {
  if (value === true || value === 1 || value === "1" || value === "true") return true;
  if (value === false || value === 0 || value === "0" || value === "false") return false;
  return null;
}
let authHydrated = false;

function hydrateFromSSOCookies() {
  if (authHydrated || typeof document === "undefined") return;

  const tokenCookie = document.cookie
    ?.split("; ")
    .find((row) => row.startsWith("userToken="));
  const infoCookie = document.cookie
    ?.split("; ")
    .find((row) => row.startsWith("userFullInfo="));

  if (!tokenCookie || !infoCookie) return;

  let decoded;
  if (infoCookie) {
    decoded = decodeCookieString(infoCookie);
  }

  const userInfo = parseCookieJson(decoded?.userFullInfo);

  if (!userInfo?.api_token) return;

  // Clean the eligibility value before it enters the app (see normalizeEligibility)
  userInfo.is_eligible_mentor = normalizeEligibility(userInfo.is_eligible_mentor);

  const storedToken = localStorage.getItem("userToken");
  const reduxState = storeConfig.store.getState();
  const reduxUser = reduxState?.userInfo;

  // Same user = same id AND same email
  const sameUser =
    reduxUser?.id === userInfo.id && reduxUser?.email === userInfo.email;

  // Same token AND same user AND already logged in — nothing to do
  if (storedToken && storedToken === userInfo.api_token && sameUser && reduxState?.isAuth)
    return;

  // A DIFFERENT user logged in — remove every trace of the previous user first
  if (reduxUser?.id && !sameUser) {
    [
      "persist:root",
      "userToken",
      "userRole",
      "userInfo",
      "isConfirmed",
      "profileImage",
      "profile_img",
      "mentorEligibilityPopupShown",
    ].forEach((key) => localStorage.removeItem(key));
  }

  // Hydrate the new user into localStorage + Redux
  localStorage.setItem("userToken", userInfo.api_token);
  localStorage.setItem("isConfirmed", "true");

  storeConfig.store.dispatch(loginDataSuccess(userInfo));
  storeConfig.store.dispatch(loginUserLoginSuccess(true));

  authHydrated = true;
}

hydrateFromSSOCookies();

// Fix redux-persist race condition: the module-load hydration above dispatches
// to Redux, but persistStore()'s async REHYDRATE overwrites it with defaults
// (empty persist:root in fresh sessions). Subscribe to the store and re-run
// hydration once after persist finishes, so the dispatch sticks.

const unsubscribeRehydrate = storeConfig.store.subscribe(() => {
  const state = storeConfig.store.getState();
  if (state._persist?.rehydrated) {
    unsubscribeRehydrate();
    // Always re-run once after redux-persist finishes. With change 1b, this
    // detects that the saved user is different from the cookie user and fixes it.
    authHydrated = false;
    hydrateFromSSOCookies();
  }
});

agent.interceptors.request.use((config) => {
  hydrateFromSSOCookies();

  const token = localStorage.getItem("userToken");

  if (!token) return config;

  return {
    ...config,
    headers: {
      ...config.headers,
      "X-Env": xSimVAlue,
      Authorization: `Bearer ${token}`,
    },
  };
});

agent.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const currentPath = globalThis.location.pathname;

    // Prevent infinite redirect loop
    if ((status === 401 || status === 403) && currentPath !== "/login") {
      localStorage.clear();

      // Force redirect only once
      globalThis.location.href = "/login";
    }

    return Promise.reject(error?.response?.data || error);
  },
);

export { agent };
